// ============================================================================
// Widget démo publique de l'Assistant IA Gestimmony (3 questions sans compte).
// Flottant en bas à droite de la landing. Limite appliquée côté serveur par IP.
// ============================================================================
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Sparkles, Send, X, Loader2, MessageSquare, ArrowRight } from "lucide-react";

const STARTERS = [
  "Comment calculer la révision annuelle du loyer avec l'IRL ?",
  "Mon locataire ne paie pas, quelles sont mes options ?",
  "Micro-BIC ou réel pour une location meublée ?",
];

// Rendu léger du markdown de l'assistant : **gras** + sauts de ligne.
function renderRich(text) {
  if (!text) return null;
  return text.split("\n").map((line, li) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
      p.startsWith("**") && p.endsWith("**")
        ? <strong key={i}>{p.slice(2, -2)}</strong>
        : <span key={i}>{p}</span>
    );
    return <div key={li}>{parts}</div>;
  });
}

export default function DemoAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && remaining === null) {
      api.get("/public/assistant/quota").then(({ data }) => setRemaining(data.remaining)).catch(() => setRemaining(3));
    }
  }, [open, remaining]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const ask = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    if (remaining !== null && remaining <= 0) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q }]);
    setBusy(true);
    try {
      const { data } = await api.post("/public/assistant/demo", { message: q });
      setMessages((m) => [...m, { role: "assistant", content: data.answer }]);
      setRemaining(data.remaining);
    } catch (e) {
      const status = e.response?.status;
      if (status === 429) {
        setRemaining(0);
        setMessages((m) => [...m, { role: "assistant", content: "Vous avez utilisé vos 3 questions de démonstration. Créez un compte pour discuter sans limite avec l'assistant complet. 🙂" }]);
      } else {
        setMessages((m) => [...m, { role: "assistant", content: "Désolé, une erreur est survenue. Réessayez dans un instant." }]);
      }
    } finally { setBusy(false); }
  };

  const exhausted = remaining !== null && remaining <= 0;

  return (
    <>
      {/* Bouton flottant */}
      {!open && (
        <button
          data-testid="demo-assistant-open"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-50 flex items-center justify-center gap-2 rounded-full bg-[hsl(var(--primary))] text-white shadow-xl hover:scale-105 transition-transform h-12 w-12 sm:w-auto sm:h-auto sm:pl-4 sm:pr-5 sm:py-3"
        >
          <Sparkles className="h-5 w-5 shrink-0" />
          <span className="hidden sm:inline font-semibold text-sm">Tester l'assistant IA</span>
        </button>
      )}

      {/* Panneau */}
      {open && (
        <div
          data-testid="demo-assistant-panel"
          className="fixed bottom-5 right-5 z-50 w-[92vw] max-w-sm h-[70vh] max-h-[560px] bg-white rounded-2xl shadow-2xl border border-stone-200 flex flex-col overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 bg-[hsl(var(--primary))] text-white">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-white/15 flex items-center justify-center">
                <Sparkles className="h-4 w-4" />
              </div>
              <div>
                <div className="font-heading font-bold text-sm leading-tight">Assistant Gestimmony</div>
                <div className="text-[10px] text-white/70">
                  {remaining === null ? "Démo gratuite" : `${remaining} question(s) gratuite(s) restante(s)`}
                </div>
              </div>
            </div>
            <button data-testid="demo-assistant-close" onClick={() => setOpen(false)} className="text-white/80 hover:text-white">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-stone-50" data-testid="demo-assistant-messages">
            {messages.length === 0 && (
              <div className="text-center py-4">
                <div className="h-12 w-12 mx-auto rounded-2xl bg-teal-100 text-teal-700 flex items-center justify-center mb-3">
                  <MessageSquare className="h-6 w-6" />
                </div>
                <p className="text-sm text-slate-600">Posez une question sur le droit locatif, la fiscalité ou la gestion de vos biens.</p>
                <div className="mt-4 space-y-2">
                  {STARTERS.map((s, i) => (
                    <button
                      key={i}
                      data-testid={`demo-starter-${i}`}
                      onClick={() => ask(s)}
                      className="w-full text-left text-xs bg-white border border-slate-200 rounded-lg px-3 py-2 hover:border-teal-400 transition"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed ${
                  m.role === "user" ? "bg-[hsl(var(--primary))] text-white" : "bg-white border border-slate-200 text-slate-800"
                }`}>
                  {m.role === "assistant" ? renderRich(m.content) : m.content}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" /> L'assistant réfléchit…
              </div>
            )}
          </div>

          {exhausted ? (
            <div className="p-4 border-t border-stone-200 bg-white">
              <Link
                to="/register"
                data-testid="demo-assistant-cta"
                className="w-full inline-flex items-center justify-center gap-2 bg-[hsl(var(--primary))] text-white font-semibold rounded-full px-4 py-2.5 text-sm hover:opacity-90 transition"
              >
                Créer un compte gratuit <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ) : (
            <div className="p-3 border-t border-stone-200 bg-white">
              <div className="flex items-end gap-2">
                <textarea
                  data-testid="demo-assistant-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
                  placeholder="Votre question…"
                  rows={1}
                  disabled={busy}
                  className="flex-1 resize-none text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                />
                <button
                  data-testid="demo-assistant-send"
                  onClick={() => ask()}
                  disabled={busy || !input.trim()}
                  className="h-9 w-9 rounded-lg bg-[hsl(var(--primary))] text-white flex items-center justify-center disabled:opacity-40"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
